"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { Spinner } from "../../../components/ui/spinner";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../..//components/ui/command";
import { useSelector } from "react-redux";
import {
  selectLanguagesData,
  selectLanguagesError,
  selectLanguagesLoading,
  selectPurchasedLanguages,
} from "../../../redux/selectors/languageSelectors";
import { Badge } from "../../../components/ui/badge";
import { formatLanguageName } from "../../../lib/utils";

function ChooseLanguageAndCheckout({
  handleCtaClick,
  loading: parentLoading,
  tier,
  pricePerLanguage = 1,
  frequencies = [], // Array of frequency options (e.g., [{ value: "monthly", label: "Monthly" }, { value: "annually", label: "Yearly" }])
  setFrequency,
  frequency,
  selectedLanguages,
  setSelectedLanguages,
  isFrequencyLocked = false, // New prop to lock frequency selection
  onClose = () => {}, // Callback when dialog closes
}) {
  const [isOpen, setIsOpen] = useState(true); // Dialog open by default
  const languages = useSelector(selectLanguagesData);
  const loading = useSelector(selectLanguagesLoading) || parentLoading;
  const error = useSelector(selectLanguagesError);
  const purchasedLanguages = useSelector(selectPurchasedLanguages);
  const purchasedLanguageIds = purchasedLanguages.map((item) => item.id);
  const purchasedSet = new Set(purchasedLanguageIds);
  const alreadyPurchased = languages.filter((language) =>
    purchasedSet.has(language.id)
  );
  const neededLanguages = languages.filter(
    (language) => !purchasedSet.has(language.id)
  );
  
  const handleLanguageSelect = (language) => {
    setSelectedLanguages((prevSelected) => {
      const isSelected = prevSelected.some((lang) => lang.id === language.id);
      return isSelected
        ? prevSelected.filter((lang) => lang.id !== language.id)
        : [...prevSelected, language];
    });
  };

  const calculateTotalCost = () => {
    // pricePerLanguage is already the full price (1 for monthly, 10 for annual)
    return selectedLanguages.length * pricePerLanguage;
  };

  const billingText = frequency.value === "2" ? "/ year" : "/ month";

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (loading) {
    return <Spinner className="mt-6" />;
  }

  if (error) {
    return <p>Error: Could not load languages.</p>;
  }

  if (!languages || languages.length === 0) {
    return <p>No languages available.</p>;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) onClose();
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Select Languages
            <p className="text-2xl my-3">
              Total: ${calculateTotalCost()} {billingText}
            </p>
          </DialogTitle>
          <DialogDescription>
            Choose the languages you want to learn. Each language is $
            {pricePerLanguage}{" "}
            {frequency.value === "2" ? "per year" : "per month"}.
          </DialogDescription>{" "}
          {!isFrequencyLocked && (
            <div className="flex justify-center">
              <RadioGroup
                defaultValue={frequency.value}
                onValueChange={(value) => {
                  setFrequency(frequencies.find((f) => f.value === value));
                }}
                className="grid gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 bg-accent dark:bg-black ring-1 ring-inset ring-white/30 dark:ring-white"
                style={{
                  gridTemplateColumns: `repeat(${frequencies.length}, minmax(0, 1fr))`,
                }}
              >
                <Label className="sr-only">Payment frequency</Label>{" "}
                {/* Changed to Chakra Label */}
                {frequencies.map((option) => (
                  <Label // Keep Label from your UI library for consistent styling
                    key={option.value}
                    htmlFor={option.value}
                    className={`cursor-pointer rounded-full px-2.5 py-2 transition-all ${
                      frequency.value === option.value
                        ? "bg-indigo-500/90 text-white dark:bg-indigo-900/70 dark:text-white/70"
                        : "bg-transparent text-gray-800 hover:bg-indigo-500/10"
                    }`}
                  >
                    {option.label}
                    <RadioGroupItem
                      value={option.value}
                      id={option.value}
                      className="hidden"
                    />
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}
        </DialogHeader>
        <Command>
          <CommandInput placeholder="Search languages..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup heading="Already">
              {alreadyPurchased.length === 0 ? (
                <p className="px-2 py-2 text-sm text-muted-foreground">
                  No purchased languages yet.
                </p>
              ) : (
                alreadyPurchased.map((language) => (
                  <CommandItem
                    key={language.id || language.name}
                    className="flex items-center py-0"
                  >
                    <Checkbox
                      id={`language-${language.id || language.name}`}
                      disabled
                      checked
                    />
                    <Label
                      htmlFor={`language-${language.id || language.name}`}
                      className="text-sm flex justify-between py-2 px-2 w-full font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {formatLanguageName(language.name)} <Badge>Purchased</Badge>
                    </Label>
                  </CommandItem>
                ))
              )}
            </CommandGroup>
            <CommandGroup heading="Needed">
              {neededLanguages.map((language) => (
                <CommandItem
                  key={language.id || language.name}
                  className="flex items-center py-0"
                >
                  <Checkbox
                    id={`language-${language.id || language.name}`}
                    checked={selectedLanguages.some(
                      (lang) => lang.id === language.id
                    )}
                    onCheckedChange={() => handleLanguageSelect(language)}
                  />
                  <Label
                    htmlFor={`language-${language.id || language.name}`}
                    className="text-sm flex justify-between py-2 px-2 w-full font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {formatLanguageName(language.name)}
                  </Label>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <DialogFooter>
          <div className="flex justify-between items-center w-full">
            <p className="text-sm font-medium">
              Total: ${calculateTotalCost()} {billingText}
            </p>
            <Button
              size="lg"
              className="rounded-xl"
              onClick={() => handleCtaClick(selectedLanguages, frequency.value)} // Send frequency value
              disabled={loading || selectedLanguages.length === 0}
            >
              {loading ? <Spinner className="mr-2" /> : null}
              {tier.cta || "Save Languages"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ChooseLanguageAndCheckout;
